import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Bell, Check, MessageSquare, UserPlus, Inbox, Activity, RefreshCw } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';

const TYPE_ICONS = {
  new_request: Inbox,
  status_changed: Activity,
  assigned: UserPlus,
  comment: MessageSquare,
  news_comment: MessageSquare,
  approval_requested: UserPlus,
  approved: Check,
  rejected: Check,
};

const POLL_INTERVAL_MS = 30000;

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const pollRef = useRef(null);

  const fetchCount = useCallback(async () => {
    try {
      const res = await axios.get('/notifications/count');
      setUnreadCount(res.data.count || 0);
    } catch (err) {
      // Silent: user might be offline
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/notifications');
      setNotifications(res.data || []);
    } catch (err) {
      // Silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCount();
    pollRef.current = setInterval(fetchCount, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchCount]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  const markAllRead = async () => {
    try {
      await axios.post('/notifications/read', []);
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (err) {
      // Silent
    }
  };

  const handleClick = async (n) => {
    try {
      if (!n.is_read) {
        await axios.post('/notifications/read', [n.id]);
        setUnreadCount(c => Math.max(0, c - 1));
        setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
      }
    } catch {}
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const renderTimeAgo = (iso) => {
    try {
      return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: de });
    } catch {
      return '';
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          data-testid="notification-bell-btn"
          aria-label="Benachrichtigungen"
        >
          <Bell className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          {unreadCount > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -top-0.5 -right-0.5 bg-[#e30613] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-96 p-0 max-h-[80vh] overflow-hidden flex flex-col"
        data-testid="notification-dropdown"
      >
        <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            <span className="font-semibold text-sm">Benachrichtigungen</span>
            {unreadCount > 0 && (
              <span className="bg-[#e30613] text-white text-[10px] font-bold rounded-full px-1.5 py-0.5">
                {unreadCount} neu
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={markAllRead}
              data-testid="mark-all-read-btn"
            >
              <Check className="w-3 h-3 mr-1" /> Alle gelesen
            </Button>
          )}
        </div>

        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-gray-400">
              <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Lade...
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
              Keine Benachrichtigungen
            </div>
          ) : (
            notifications.map(n => {
              const Icon = TYPE_ICONS[n.type] || Bell;
              return (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  data-testid={`notification-item-${n.id}`}
                  className={`w-full text-left px-3 py-2.5 flex items-start gap-3 border-b border-gray-100 dark:border-gray-800 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 ${
                    !n.is_read ? 'bg-blue-50/40 dark:bg-blue-950/20' : ''
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      n.is_read ? 'bg-gray-100 dark:bg-gray-800' : 'bg-[#e30613]/10'
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${n.is_read ? 'text-gray-400' : 'text-[#e30613]'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {!n.is_read && (
                        <span className="w-1.5 h-1.5 rounded-full bg-[#e30613] flex-shrink-0" />
                      )}
                      <p className={`text-sm truncate ${!n.is_read ? 'font-semibold' : 'font-normal'}`}>
                        {n.title}
                      </p>
                    </div>
                    {n.message && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                        {n.message}
                      </p>
                    )}
                    <p className="text-[10px] text-gray-400 mt-1">
                      {renderTimeAgo(n.created_at)}
                    </p>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
